import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import { parseIpList, classifyReserved } from '../utils/ipIntel';
import { useIpDatasets, lookupLocal, formatAge, DATASETS } from '../utils/ipIntelDatasets';
import {
  enrichBatch, enrichIp, geoAgreement, exoneratorUrl, LIVE_SOURCES,
  fetchOwnIp, recordLookups, getLookupsToday, DAILY_SOFT_LIMIT, detectNamedVpnProvider,
} from '../utils/ipIntelLive';
import { parseDomainList, lookupDomainBatch } from '../utils/domainIntel';
import { extractTextFromFile } from '../utils/extractText';

// IP and Domain Intelligence.
//
// Two deliberately separated tiers:
//   1. Offline checks (VPN / datacenter / Tor / abuse reputation / reserved
//      space) run against public lists cached in the browser. The address being
//      investigated is never transmitted.
//   2. Live enrichment (RDAP, geolocation, PTR, BGP, open ports) sends the
//      address to named third parties, and is opt-in per lookup.
// The UI must never blur that line — see the banner and the per-row controls.

const PAGE_SIZE = 100;
const INK = '#0f172a';
const MUTED = '#64748b';
const BORDER = '#e2e8f0';

// ─── Small presentational pieces ────────────────────────────────────────────

function Badge({ text, color, bg, title }) {
  return (
    <span title={title} style={{
      display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: '.06em',
      textTransform: 'uppercase', padding: '2px 8px', borderRadius: 20,
      background: bg, color, border: `1px solid ${color}44`, whiteSpace: 'nowrap',
    }}>
      {text}
    </span>
  );
}

function KVRow({ label, value, mono = false }) {
  if (value == null || value === '' || (Array.isArray(value) && !value.length)) return null;
  return (
    <tr>
      <td style={{ padding: '5px 12px 5px 0', color: MUTED, fontSize: 12.5, whiteSpace: 'nowrap', verticalAlign: 'top', fontWeight: 500 }}>
        {label}
      </td>
      <td style={{ padding: '5px 0', fontSize: 12.5, color: INK, fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-word' }}>
        {Array.isArray(value) ? value.join(', ') : value}
      </td>
    </tr>
  );
}

function Section({ title, source, children }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 800, letterSpacing: '-0.2px' }}>{title}</h4>
        {source && <span style={{ fontSize: 10.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>{source}</span>}
      </div>
      {children}
    </div>
  );
}

function Disclosure() {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
      borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, lineHeight: 1.55,
    }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        style={{ flexShrink: 0, marginTop: 1 }}>
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <span>
        <strong>This tool can send data over the network — unlike the rest of LE Tech Tools.</strong>{' '}
        The <strong>Screen</strong> step is confidential: VPN, datacenter, Tor, iCloud Private Relay and
        abuse-reputation checks run against public lists already downloaded to this browser, so the addresses
        you paste are never transmitted. <strong>Live lookup</strong> is different — it sends the individual IP you select to
        third-party services (listed under each result) to fetch registration, geolocation, hostname and
        open-port data. Nothing else you enter here leaves your device, and no address is ever stored by us.
      </span>
    </div>
  );
}

function DatasetChip({ datasets }) {
  const { meta, status, oldestFetchedAt, isStale, complete, refresh, clear } = datasets;
  const [cleared, setCleared] = useState(false);
  const refreshing = status === 'refreshing';
  const bad = !complete || isStale;
  const errored = Object.values(meta).filter(m => m && m.error);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 14px',
      background: bad ? '#fffbeb' : '#f0fdf4',
      border: `1px solid ${bad ? '#fde68a' : '#bbf7d0'}`,
      color: bad ? '#92400e' : '#166534',
      borderRadius: 10, padding: '9px 14px', marginBottom: 16, fontSize: 12.5,
    }}>
      <strong style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
          background: refreshing ? '#f59e0b' : (bad ? '#d97706' : '#16a34a'),
        }} />
        {refreshing ? 'Updating datasets…'
          : !complete ? 'Datasets incomplete'
          : isStale ? `Datasets ${formatAge(oldestFetchedAt)} — may be out of date`
          : `Datasets current · updated ${formatAge(oldestFetchedAt)}`}
      </strong>
      {Object.values(DATASETS).map(spec => {
        const m = meta[spec.id];
        return (
          <span
            key={spec.id}
            style={{ color: m && !m.error ? 'inherit' : '#b45309', opacity: 0.9 }}
            title={spec.snapshotDate ? `${spec.attribution} — bundled snapshot, not a live fetch (source: ${spec.sourceUrl})` : spec.attribution}
          >
            {spec.label}: {m && m.count ? m.count.toLocaleString() : '—'}
            {spec.snapshotDate && <> (snapshot: {spec.snapshotDate})</>}
          </span>
        );
      })}
      <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
        <button
          onClick={async () => { await clear(); setCleared(true); }}
          disabled={refreshing}
          title="Delete the cached lists from this browser. Nothing is re-downloaded until you press Refresh or reload."
          style={{
            background: 'none', border: 'none', padding: '3px 4px', fontSize: 12,
            color: 'inherit', opacity: refreshing ? 0.4 : 0.75, textDecoration: 'underline',
            cursor: refreshing ? 'default' : 'pointer',
          }}
        >
          Clear cache
        </button>
        <button
          onClick={refresh}
          disabled={refreshing}
          style={{
            background: '#fff', border: `1px solid ${bad ? '#fcd34d' : '#bbf7d0'}`,
            borderRadius: 6, padding: '3px 11px', fontSize: 12, fontWeight: 600,
            color: 'inherit', cursor: refreshing ? 'default' : 'pointer', opacity: refreshing ? 0.6 : 1,
          }}
        >
          {refreshing ? 'Updating…' : 'Refresh'}
        </button>
      </span>
      {cleared && !refreshing && (
        <span style={{ flexBasis: '100%', fontSize: 11.5, opacity: 0.9 }}>
          Cache deleted — nothing is stored in this browser right now. Note that <strong>reloading the page
          re-downloads the lists automatically</strong>, so the chip will read “current” again straight after;
          to watch a cold download, press Refresh here, or reload with the Network tab open.
        </span>
      )}
      {errored.length > 0 && (
        <span style={{ flexBasis: '100%', fontSize: 11.5, opacity: 0.85 }}>
          Could not download: {errored.length} list(s). Cached data is still being used where available —
          missing checks are shown as “unknown”, never as “clean”.
        </span>
      )}
    </div>
  );
}

// ─── Domain / email results ─────────────────────────────────────────────────

function DomainCard({ entry, result, indexes }) {
  if (!result) {
    return (
      <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '14px 18px', color: MUTED, fontSize: 13 }}>
        <strong style={{ color: INK, fontFamily: 'monospace' }}>{entry.input}</strong> — looking up…
      </div>
    );
  }

  const rdap = result.rdap.data;
  const provider = result.provider;
  const allIps = [...result.addresses.v4, ...result.addresses.v6];

  return (
    <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '16px 18px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <span style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700 }}>{entry.input}</span>
        {entry.kind === 'email' && (
          <span style={{ fontSize: 11.5, color: MUTED }}>
            mailbox <strong>{entry.mailbox}</strong> at <strong>{entry.domain}</strong> — only the domain was sent
          </span>
        )}
      </div>

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))' }}>
        {/* The headline finding: who to serve legal process on for the mailbox. */}
        <Section title="Mail handling" source="MX records">
          {!result.receivesMail ? (
            <p style={{ margin: 0, fontSize: 12.5, color: '#b45309', lineHeight: 1.6 }}>
              <strong>No MX records — this domain cannot receive mail.</strong> Common for parked domains and for
              domains forged in a spoofed “From” header. {result.spf === 'v=spf1 -all' && 'Its SPF record is “-all”, an explicit declaration that it sends no mail either.'}
            </p>
          ) : (
            <>
              {provider ? (
                <div style={{ marginBottom: 9 }}>
                  <Badge
                    text={provider.gateway ? 'Security gateway' : 'Mail provider'}
                    color={provider.gateway ? TONES.amber.color : TONES.green.color}
                    bg={provider.gateway ? TONES.amber.bg : TONES.green.bg}
                  />
                  <div style={{ fontSize: 14, fontWeight: 700, margin: '5px 0 0' }}>{provider.name}</div>
                  <p style={{ margin: '5px 0 0', fontSize: 12, color: MUTED, lineHeight: 1.55 }}>
                    {provider.gateway
                      ? 'This is a filtering layer in front of the real mailbox — the hosting provider behind it is not visible in DNS. Legal process for message content goes to whoever is behind this, not to the gateway.'
                      : provider.selfHosted
                        ? 'Mail is handled by a server inside this domain, so the domain owner holds the mailbox directly.'
                        : 'Legal process for mailbox contents goes to this provider, not to the domain registrar.'}
                  </p>
                </div>
              ) : (
                <p style={{ margin: '0 0 9px', fontSize: 12.5, color: MUTED, lineHeight: 1.55 }}>
                  Mail provider not recognised from these MX hosts — check who operates the hostnames below.
                </p>
              )}
              <table style={{ borderCollapse: 'collapse' }}>
                <tbody>
                  {result.mx.records.slice(0, 6).map(m => (
                    <KVRow key={m.host} label={`MX ${m.priority}`} value={m.host} mono />
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Section>

        <Section title="Registration" source={result.rdap.error ? 'RDAP — unavailable' : 'RDAP (WHOIS successor)'}>
          {result.rdap.error ? (
            <p style={{ margin: 0, fontSize: 12.5, color: '#b45309', lineHeight: 1.6 }}>{result.rdap.error}</p>
          ) : (
            <table style={{ borderCollapse: 'collapse' }}>
              <tbody>
                <KVRow label="Registrar" value={rdap.registrarName} />
                <KVRow label="Registered" value={rdap.registered} />
                <KVRow label="Expires" value={rdap.expires} />
                <KVRow label="Last changed" value={rdap.lastChanged} />
                <KVRow label="Registrant" value={rdap.registrantName || rdap.registrantOrg || 'Redacted for privacy (GDPR/ICANN)'} />
                <KVRow label="Registrant email" value={rdap.registrantEmail} mono />
                <KVRow label="Abuse contact" value={rdap.abuseEmail} mono />
                <KVRow label="Abuse phone" value={rdap.abusePhone && rdap.abusePhone.replace(/^tel:/, '')} />
                <KVRow label="DNSSEC" value={rdap.dnssec ? 'Signed' : 'Not signed'} />
                <KVRow label="Status" value={rdap.status.join(', ')} />
              </tbody>
            </table>
          )}
        </Section>

        <Section title="Infrastructure" source="DNS">
          <table style={{ borderCollapse: 'collapse' }}>
            <tbody>
              <KVRow label="Nameservers" value={(rdap && rdap.nameservers.length ? rdap.nameservers : result.nameservers).slice(0, 4)} mono />
              <KVRow label="SPF" value={result.spf} mono />
              <KVRow label="DMARC" value={result.dmarc} mono />
            </tbody>
          </table>
          {allIps.length > 0 && (
            <div style={{ marginTop: 10, borderTop: `1px solid ${BORDER}`, paddingTop: 9 }}>
              <div style={{ fontSize: 11, color: MUTED, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
                Resolves to — screened offline
              </div>
              {allIps.slice(0, 6).map(ip => {
                const local = { ...lookupLocal(ip, indexes), reserved: classifyReserved(ip) };
                const a = assess(local);
                return (
                  <div key={ip} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{ip}</span>
                    <Badge text={a.label} color={TONES[a.tone].color} bg={TONES[a.tone].bg} />
                    {local.threatListCount > 0 && (
                      <Badge text={`${local.threatListCount} blocklists`} color={TONES.red.color} bg={TONES.red.bg} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      </div>

      {entry.kind === 'email' && (
        <p style={{ margin: '12px 0 0', fontSize: 11.5, color: MUTED, lineHeight: 1.55 }}>
          None of this confirms the mailbox exists or who uses it — that requires legal process to the mail provider
          named above. The mailbox name was never transmitted.
        </p>
      )}
    </div>
  );
}

// ─── Own-connection check ───────────────────────────────────────────────────

// Answers "what does a website see when I connect right now, and is my VPN
// actually up?" — the check an investigator wants before doing anything online
// from an agency network.
function SelfCheck({ datasets }) {
  const [state, setState] = useState('idle'); // idle | loading | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const run = useCallback(async () => {
    setState('loading');
    setError('');
    try {
      const ip = await fetchOwnIp();
      recordLookups(1);
      const live = await enrichIp(ip);
      setResult({ ip, live });
      setState('done');
    } catch (err) {
      setError(err.message || 'Could not check this connection');
      setState('error');
    }
  }, []);

  const local = result
    ? { ...lookupLocal(result.ip, datasets.indexes), reserved: classifyReserved(result.ip) }
    : null;

  const namedVpn = result && detectNamedVpnProvider(result.live);

  // Deliberately blunt wording — this is a go/no-go check, not an analysis.
  const verdict = !local ? null
    : local.isTor ? { tone: 'purple', text: 'Your traffic is exiting through a Tor relay.' }
    : local.isAppleRelay ? { tone: 'green', text: 'This is an Apple iCloud Private Relay egress address — your real address is not what sites see.' }
    : namedVpn ? { tone: 'green', text: `This is a ${namedVpn.provider} address (from its own ${namedVpn.source}) — your real address is not what sites see.` }
    : local.isVpn ? { tone: 'green', text: 'This is a known VPN exit — your real address is not what sites see.' }
    : (local.cloudProvider || local.isDatacenter)
      ? { tone: 'amber', text: `This is a hosting/datacenter address${local.cloudProvider ? ` (${local.cloudProvider})` : ''} — consistent with a VPN, but not on any VPN list.` }
      : { tone: 'red', text: 'This looks like an ordinary ISP connection, NOT a VPN or proxy. Sites you visit see this address and its subscriber network.' };

  const geo = result && result.live.geo.find(g => g.data);
  const rdap = result && result.live.rdap.data;

  return (
    <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '14px 18px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ flex: '1 1 300px' }}>
          <strong style={{ fontSize: 13.5 }}>Check my own connection</strong>
          <p style={{ margin: '3px 0 0', fontSize: 12.5, color: MUTED, lineHeight: 1.5 }}>
            Shows what a website sees when you connect right now, and whether it looks like a VPN. Worth running
            before any online work where the agency&rsquo;s address shouldn&rsquo;t be visible.
          </p>
        </div>
        <button
          onClick={run}
          disabled={state === 'loading'}
          style={{
            background: state === 'loading' ? '#cbd5e1' : '#0f766e', color: '#fff', border: 'none',
            borderRadius: 8, padding: '9px 18px', fontSize: 13.5, fontWeight: 700,
            cursor: state === 'loading' ? 'default' : 'pointer', whiteSpace: 'nowrap',
          }}
        >
          {state === 'loading' ? 'Checking…' : state === 'done' ? 'Check again' : 'Check my connection'}
        </button>
      </div>

      {state === 'error' && (
        <p style={{ margin: '10px 0 0', fontSize: 12.5, color: '#b45309' }}>{error}</p>
      )}

      {state === 'done' && result && (
        <div style={{
          marginTop: 12, borderTop: `1px solid ${BORDER}`, paddingTop: 12,
          display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        }}>
          <div>
            <div style={{ fontSize: 11, color: MUTED, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>
              Your public address
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, margin: '3px 0 6px' }}>{result.ip}</div>
            <Badge text={verdict.tone === 'red' ? 'No VPN detected' : 'Anonymized'} color={TONES[verdict.tone].color} bg={TONES[verdict.tone].bg} />
          </div>
          <div style={{ fontSize: 12.5, color: '#334155', lineHeight: 1.6 }}>
            <div><span style={{ color: MUTED }}>Seen as: </span>{rdap ? (rdap.registrantName || rdap.netName) : '—'}</div>
            <div><span style={{ color: MUTED }}>Located: </span>{geo ? [geo.data.city, geo.data.region, geo.data.country].filter(Boolean).join(', ') : '—'}</div>
            <div><span style={{ color: MUTED }}>Hostname: </span><span style={{ fontFamily: 'monospace', fontSize: 11.5 }}>{result.live.ptr.data && result.live.ptr.data.hostnames[0] ? result.live.ptr.data.hostnames[0] : 'none'}</span></div>
          </div>
          <div style={{
            fontSize: 12.5, lineHeight: 1.55, padding: '9px 12px', borderRadius: 8,
            background: TONES[verdict.tone].bg, color: TONES[verdict.tone].color,
            border: `1px solid ${TONES[verdict.tone].color}33`,
          }}>
            {verdict.text}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Assessment ─────────────────────────────────────────────────────────────

// A plain-language verdict, offline signals first. `live` is optional — most
// rows haven't been opted into live lookup yet — and when present it can name
// a specific VPN provider the offline lists only knew as generic "VPN" or
// missed outright (see detectNamedVpnProvider). Ordered most to least
// specific; "unknown" is a real outcome when the lists didn't load.
function assess(local, live) {
  if (local.reserved) return { label: local.reserved.label, tone: 'slate' };
  // Tor and the Apple relay list are matched by address / BigInt range rather
  // than the Uint32 tables, so both apply to IPv6 too and are checked before
  // the IPv4-only lists bow out.
  if (local.isTor) return { label: 'Tor exit', tone: 'purple' };
  if (local.isAppleRelay) return { label: 'iCloud Private Relay', tone: 'green' };
  const named = detectNamedVpnProvider(live);
  if (named) return { label: `${named.provider} (VPN)`, tone: 'amber' };
  if (!local.ipv4Supported) return { label: 'IPv6 — most lists n/a', tone: 'slate' };
  if (local.isVpn) return { label: 'VPN', tone: 'amber' };
  if (local.cloudProvider) return { label: local.cloudProvider, tone: 'blue' };
  if (local.isDatacenter) return { label: 'Datacenter / hosting', tone: 'blue' };
  // Distinguish "the lists say no" from "the lists never loaded".
  if (local.isVpn === null && local.isDatacenter === null) return { label: 'Not screened', tone: 'slate' };
  return { label: 'No anonymizer match', tone: 'green' };
}

const TONES = {
  green:  { color: '#15803d', bg: '#f0fdf4' },
  amber:  { color: '#b45309', bg: '#fffbeb' },
  blue:   { color: '#0369a1', bg: '#e0f2fe' },
  purple: { color: '#7c3aed', bg: '#f3e8ff' },
  red:    { color: '#dc2626', bg: '#fef2f2' },
  slate:  { color: '#475569', bg: '#f1f5f9' },
};

function threatTone(count) {
  if (!count) return 'green';
  if (count >= 5) return 'red';
  return 'amber';
}

// ─── Detail panel ───────────────────────────────────────────────────────────

function GeoComparison({ geo }) {
  const agreement = geoAgreement(geo);
  const warn = agreement.level === 'country-conflict' || agreement.level === 'city-conflict';

  return (
    <Section title="Approximate location" source="3 independent providers">
      {warn && (
        <div style={{
          background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
          borderRadius: 7, padding: '7px 10px', marginBottom: 9, fontSize: 12, lineHeight: 1.5,
        }}>
          <strong>Providers disagree{agreement.level === 'country-conflict' ? ' on the country' : ' on the city'}.</strong>{' '}
          Treat this location as unreliable — it is an estimate from commercial databases, not a measurement.
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 420 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
              {['Provider', 'City', 'Region', 'Country', 'Network'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '4px 10px 6px 0', color: MUTED, fontWeight: 600, fontSize: 11 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {geo.map(r => (
              <tr key={r.source} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '5px 10px 5px 0', color: MUTED, whiteSpace: 'nowrap' }}>{r.source}</td>
                {r.error ? (
                  <td colSpan={4} style={{ padding: '5px 0', color: '#b45309' }}>{r.error}</td>
                ) : (
                  <>
                    <td style={{ padding: '5px 10px 5px 0' }}>{r.data.city || '—'}</td>
                    <td style={{ padding: '5px 10px 5px 0' }}>{r.data.region || '—'}</td>
                    <td style={{ padding: '5px 10px 5px 0' }}>{r.data.country || '—'}</td>
                    <td style={{ padding: '5px 0', color: MUTED }}>{[r.data.asn, r.data.isp].filter(Boolean).join(' · ') || '—'}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function DetailPanel({ row, onEnrich }) {
  const { ip, local, live, status } = row;
  const reserved = local.reserved;
  const namedVpn = detectNamedVpnProvider(live);

  return (
    <div style={{ background: '#f8fafc', borderTop: `1px solid ${BORDER}`, padding: '16px 18px' }}>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: MUTED, lineHeight: 1.5 }}>
        None of the screening below is the commercial database most people mean by "MaxMind." To cross-check{' '}
        <span style={{ fontFamily: 'monospace', color: INK }}>{ip}</span> against it directly, paste it into{' '}
        <a href="https://www.maxmind.com/en/geoip-demo" target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', fontWeight: 600 }}>
          MaxMind&rsquo;s own GeoIP demo ↗
        </a>{' '}— it can't be deep-linked with the address prefilled, so it takes a paste.
      </p>
      {namedVpn && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d',
          borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13, lineHeight: 1.55,
        }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            style={{ flexShrink: 0, marginTop: 1 }}>
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span>
            <strong>Named VPN provider: {namedVpn.provider}.</strong> Its {namedVpn.source} reads
            "<span style={{ fontFamily: 'monospace' }}>{namedVpn.matchedText}</span>" — this provider runs its own
            address space, so the public registry names it directly (no curated list required). This only catches
            providers operating their own network; a VPN running on space leased from a generic host won't show up
            this way.
          </span>
        </div>
      )}
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>

        {/* Tier 1 — always available, nothing sent */}
        <Section title="Offline screening" source="nothing sent">
          <table style={{ borderCollapse: 'collapse' }}>
            <tbody>
              {reserved && <KVRow label="Special use" value={`${reserved.label} (${reserved.cidr})`} />}
              <KVRow label="VPN list" value={local.isVpn === null ? 'unknown — list unavailable' : (local.isVpn ? 'MATCH' : 'no match')} />
              <KVRow label="Datacenter list" value={local.isDatacenter === null ? 'unknown — list unavailable' : (local.isDatacenter ? 'MATCH' : 'no match')} />
              <KVRow label="Cloud / CDN network" value={local.cloudProvider || (local.ipv4Supported ? 'no match' : null)} />
              <KVRow label="Tor exit list" value={local.isTor === null ? 'unknown — list unavailable' : (local.isTor ? 'MATCH' : 'no match')} />
              <KVRow
                label="Threat lists"
                value={local.threatListCount === null ? 'unknown — list unavailable'
                  : local.threatListCount ? `listed on ${local.threatListCount} blocklist(s)` : 'not listed'}
              />
              <KVRow label="iCloud Private Relay" value={local.isAppleRelay === null ? 'unknown — list unavailable' : (local.isAppleRelay ? 'MATCH — Apple relay egress address' : 'no match')} />
              {!local.ipv4Supported && <KVRow label="Note" value="IPv6 — the VPN, datacenter, and threat CIDR lists in this tool are IPv4 only, so those checks were not run. The Tor and iCloud Private Relay lists do cover IPv6 and were checked above." />}
            </tbody>
          </table>
          {reserved && (
            <p style={{ margin: '9px 0 0', fontSize: 12, color: MUTED, lineHeight: 1.55 }}>{reserved.note}</p>
          )}
        </Section>

        {local.torRelay && (
          <Section title="Tor relay record" source="Tor Project — Onionoo">
            <table style={{ borderCollapse: 'collapse' }}>
              <tbody>
                <KVRow label="Nickname" value={local.torRelay.nickname} />
                <KVRow label="Fingerprint" value={local.torRelay.fingerprint} mono />
                <KVRow label="First seen" value={local.torRelay.firstSeen} />
                <KVRow label="Last seen" value={local.torRelay.lastSeen} />
                <KVRow label="Hosted on" value={[local.torRelay.as, local.torRelay.asName].filter(Boolean).join(' — ')} />
                <KVRow label="Exit address" value={local.torRelay.isExitAddress ? 'Yes — traffic exits here' : 'Relay address (not a listed exit)'} />
                <KVRow
                  label="Relays here"
                  value={local.torRelay.relayCount > 1
                    ? `${local.torRelay.relayCount} relays share this address — details above are for one of them`
                    : null}
                />
              </tbody>
            </table>
            <p style={{ margin: '9px 0 0', fontSize: 12, color: MUTED, lineHeight: 1.55 }}>
              This reflects relays running <em>now</em>. For whether it was an exit on a specific past date, use{' '}
              <a href={exoneratorUrl(ip)} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1' }}>
                Tor Metrics ExoneraTor
              </a>{' '}— the authoritative historical record.
            </p>
          </Section>
        )}

        {/* Tier 2 — opt-in */}
        {!live && (
          <Section title="Live lookup" source="opt-in">
            {reserved ? (
              <p style={{ margin: 0, fontSize: 12.5, color: MUTED, lineHeight: 1.6 }}>
                Not applicable — {reserved.label.toLowerCase()} addresses are not registered to anyone and have no
                public record to look up.
              </p>
            ) : (
              <>
                <p style={{ margin: '0 0 10px', fontSize: 12.5, color: MUTED, lineHeight: 1.6 }}>
                  Sends <strong style={{ color: INK, fontFamily: 'monospace' }}>{ip}</strong> to:
                </p>
                <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 12, color: MUTED, lineHeight: 1.65 }}>
                  {LIVE_SOURCES.map(s => <li key={s.host}><strong style={{ color: '#475569' }}>{s.host}</strong> — {s.what}</li>)}
                </ul>
                <button
                  onClick={() => onEnrich([ip])}
                  disabled={status === 'loading'}
                  style={{
                    background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 7,
                    padding: '7px 16px', fontSize: 13, fontWeight: 600,
                    cursor: status === 'loading' ? 'default' : 'pointer', opacity: status === 'loading' ? 0.6 : 1,
                  }}
                >
                  {status === 'loading' ? 'Looking up…' : 'Run live lookup'}
                </button>
              </>
            )}
          </Section>
        )}

        {live && (
          <>
            <Section title="Registration (authoritative)" source={live.rdap.error ? 'RDAP — failed' : 'RDAP'}>
              {live.rdap.error ? (
                <p style={{ margin: 0, fontSize: 12.5, color: '#b45309' }}>{live.rdap.error}</p>
              ) : (
                <table style={{ borderCollapse: 'collapse' }}>
                  <tbody>
                    <KVRow label="Network" value={live.rdap.data.netName} />
                    <KVRow label="Range" value={live.rdap.data.cidr || [live.rdap.data.startAddress, live.rdap.data.endAddress].filter(Boolean).join(' – ')} mono />
                    <KVRow label="Registered to" value={live.rdap.data.registrantName} />
                    <KVRow label="Country" value={live.rdap.data.country} />
                    <KVRow label="Allocation type" value={live.rdap.data.type} />
                    <KVRow label="Registered" value={live.rdap.data.registered} />
                    <KVRow label="Last changed" value={live.rdap.data.lastChanged} />
                    <KVRow label="Handle" value={live.rdap.data.handle} mono />
                    <KVRow label="Abuse contact" value={live.rdap.data.abuseEmail || (live.ripestat.data && live.ripestat.data.contacts.join(', '))} mono />
                    <KVRow label="Abuse desk" value={live.rdap.data.abuseName} />
                    <KVRow label="Abuse phone" value={live.rdap.data.abusePhone} />
                    <KVRow label="Authoritative RIR" value={live.ripestat.data && live.ripestat.data.authoritativeRir} />
                    <KVRow label="Remarks" value={live.rdap.data.remarks} />
                  </tbody>
                </table>
              )}
            </Section>

            <GeoComparison geo={live.geo} />

            <Section title="Naming & routing" source="dns.google · Team Cymru">
              <table style={{ borderCollapse: 'collapse' }}>
                <tbody>
                  <KVRow
                    label="Reverse DNS"
                    value={live.ptr.error ? live.ptr.error : (live.ptr.data.hostnames.length ? live.ptr.data.hostnames : 'no PTR record')}
                    mono
                  />
                  {live.bgp.data && <>
                    <KVRow label="Announced by" value={[live.bgp.data.asn, live.bgp.data.asName].filter(Boolean).join(' — ')} />
                    <KVRow label="BGP prefix" value={live.bgp.data.prefix} mono />
                    <KVRow label="Registry" value={live.bgp.data.registry} />
                    <KVRow label="Allocated" value={live.bgp.data.allocated} />
                  </>}
                  {live.bgp.error && <KVRow label="BGP origin" value={live.bgp.error} />}
                </tbody>
              </table>
              <p style={{ margin: '9px 0 0', fontSize: 11.5, color: MUTED, lineHeight: 1.5 }}>
                A reverse-DNS query resolves against the netblock owner’s own nameserver, so in principle that
                operator can observe it. The other sources here are third-party databases and are not visible
                to the address holder.
              </p>
            </Section>

            <Section title="Previously observed services" source="Shodan InternetDB">
              {live.shodan.error ? (
                <p style={{ margin: 0, fontSize: 12.5, color: '#b45309' }}>{live.shodan.error}</p>
              ) : !live.shodan.data.observed ? (
                <p style={{ margin: 0, fontSize: 12.5, color: MUTED }}>Never scanned by Shodan — no data. This is not evidence of anything.</p>
              ) : (
                <table style={{ borderCollapse: 'collapse' }}>
                  <tbody>
                    <KVRow label="Open ports" value={live.shodan.data.ports.join(', ')} mono />
                    <KVRow label="Hostnames" value={live.shodan.data.hostnames} mono />
                    <KVRow label="Software" value={live.shodan.data.cpes.slice(0, 6)} mono />
                    <KVRow label="Tags" value={live.shodan.data.tags} />
                    <KVRow label="Known CVEs" value={live.shodan.data.vulns.slice(0, 10)} mono />
                  </tbody>
                </table>
              )}
            </Section>

          </>
        )}
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function IPLookup() {
  const datasets = useIpDatasets();
  const [text, setText] = useState('');
  const [ips, setIps] = useState([]);
  const [liveByIp, setLiveByIp] = useState({});
  const [loadingIps, setLoadingIps] = useState(() => new Set());
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [notice, setNotice] = useState('');
  const [lookupsToday, setLookupsToday] = useState(() => getLookupsToday());
  const [mode, setMode] = useState('ip'); // ip | domain
  const [domainEntries, setDomainEntries] = useState([]);
  const [domainResults, setDomainResults] = useState({});
  const [domainBusy, setDomainBusy] = useState(false);
  const [fileBusy, setFileBusy] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

  // Uploaded content is only ever appended to the textarea, in either mode —
  // it never triggers screening or a live lookup by itself. Whichever button
  // the user presses afterward (offline screen, or one that sends data out)
  // still requires its own explicit click.
  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setFileBusy(true);
    try {
      const extracted = await extractTextFromFile(file);
      const count = mode === 'domain'
        ? parseDomainList(extracted).entries.length
        : parseIpList(extracted).ips.length;
      const noun = mode === 'domain' ? 'domain/email address' : 'IP address';
      setText(prev => (prev.trim() ? `${prev}\n${extracted}` : extracted));
      setNotice(`${file.name}: found ${count} ${noun}${count === 1 ? '' : 'es'}. Press the button below to run it.`);
    } catch (err) {
      setNotice(`Could not read ${file.name}: ${err.message}`);
    } finally {
      setFileBusy(false);
    }
  }, [mode]);

  // Offline verdicts. Recomputed when a dataset finishes downloading so rows
  // fill in as the lists arrive, without the user re-running anything.
  const rows = useMemo(() => ips.map(ip => ({
    ip,
    local: { ...lookupLocal(ip, datasets.indexes), reserved: classifyReserved(ip) },
    live: liveByIp[ip] || null,
    status: loadingIps.has(ip) ? 'loading' : (liveByIp[ip] ? 'done' : 'idle'),
  })), [ips, datasets.indexes, liveByIp, loadingIps]);

  // Returns the addresses it found so a caller can chain a live lookup onto it
  // without waiting for the state update to land.
  const screen = useCallback((source) => {
    const { ips: found, duplicateCount } = parseIpList(source);
    if (!found.length) {
      setNotice('No valid IP addresses found in that input.');
      return [];
    }
    trackToolAction('/iplookup', 'IP and Domain Intelligence');
    setIps(found);
    setSelected(new Set());
    setExpanded(found.length === 1 ? found[0] : null);
    setPage(0);
    setNotice(duplicateCount ? `${found.length} unique addresses (${duplicateCount} duplicate mention${duplicateCount === 1 ? '' : 's'} collapsed).` : '');
    return found;
  }, []);

  const runEnrich = useCallback(async (targets) => {
    // Reserved space has no registration to look up — don't waste a request or
    // send an internal address anywhere.
    const list = targets.filter(ip => !classifyReserved(ip) && !liveByIp[ip]);
    if (!list.length) return;

    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoadingIps(prev => new Set([...prev, ...list]));
    await enrichBatch(list, {
      signal: ctrl.signal,
      onResult: (ip, result) => {
        setLiveByIp(prev => ({ ...prev, [ip]: result }));
        setLoadingIps(prev => { const next = new Set(prev); next.delete(ip); return next; });
        // Count as they land, so an aborted batch only charges what it spent.
        setLookupsToday(recordLookups(1));
      },
    });
    setLoadingIps(prev => { const next = new Set(prev); list.forEach(ip => next.delete(ip)); return next; });
  }, [liveByIp]);

  // Screen, then immediately look up every address that has anything to look
  // up. The free services this leans on have modest daily ceilings (ipapi.is is
  // 30/day without a key) — geoIpapiIs itself refuses calls once that's spent,
  // so there's no need to gate this on a confirmation dialog first.
  const screenAndEnrich = useCallback(() => {
    const found = screen(text);
    if (!found.length) return;

    const lookupable = found.filter(ip => !classifyReserved(ip));
    if (!lookupable.length) {
      setNotice(`${found.length} address${found.length === 1 ? '' : 'es'} screened. All are private or special-use, so there is nothing to look up.`);
      return;
    }
    runEnrich(lookupable);
  }, [screen, text, runEnrich]);

  // Domains have no offline tier — there is no public dataset that answers
  // "who runs this domain's mail", so this is always a network operation.
  const runDomains = useCallback(async () => {
    const { entries, duplicateCount } = parseDomainList(text);
    if (!entries.length) {
      setNotice('No valid domains or email addresses found in that input.');
      return;
    }
    trackToolAction('/iplookup', 'IP and Domain Intelligence');
    setDomainEntries(entries);
    setDomainResults({});
    setNotice(duplicateCount ? `${entries.length} unique (${duplicateCount} duplicate${duplicateCount === 1 ? '' : 's'} collapsed).` : '');

    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setDomainBusy(true);

    // One lookup per distinct domain, even if several addresses share it.
    const domains = [...new Set(entries.map(e => e.domain))];
    await lookupDomainBatch(domains, {
      signal: ctrl.signal,
      onResult: (domain, result) => setDomainResults(prev => ({ ...prev, [domain]: result })),
    });
    setDomainBusy(false);
  }, [text]);

  const exportDomainCsv = useCallback(() => {
    const hdrs = ['Input', 'Type', 'Domain', 'Receives mail', 'Mail provider', 'Is security gateway',
      'MX records', 'Registrar', 'Registered', 'Expires', 'Registrant', 'Abuse contact',
      'Nameservers', 'SPF', 'DMARC', 'Resolves to', 'Looked up at'];
    const body = domainEntries.map(e => {
      const r = domainResults[e.domain];
      const rd = r && r.rdap.data;
      return [
        e.input, e.kind, e.domain,
        r ? (r.receivesMail ? 'yes' : 'no') : '',
        r && r.provider ? r.provider.name : '',
        r && r.provider ? (r.provider.gateway ? 'yes' : 'no') : '',
        r ? r.mx.records.map(m => `${m.priority} ${m.host}`).join(' ') : '',
        rd ? rd.registrarName : (r && r.rdap.error ? `error: ${r.rdap.error}` : ''),
        rd ? rd.registered : '', rd ? rd.expires : '',
        rd ? (rd.registrantName || rd.registrantOrg) : '',
        rd ? rd.abuseEmail : '',
        r ? (rd && rd.nameservers.length ? rd.nameservers : r.nameservers).join(' ') : '',
        r ? r.spf : '', r ? r.dmarc : '',
        r ? [...r.addresses.v4, ...r.addresses.v6].join(' ') : '',
        r ? new Date(r.lookedUpAt).toISOString() : '',
      ];
    });
    const csv = [hdrs, ...body]
      .map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: 'domain-intelligence-export.csv',
    });
    a.click();
    URL.revokeObjectURL(a.href);
  }, [domainEntries, domainResults]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      const named = Boolean(detectNamedVpnProvider(r.live));
      if (filter === 'flagged' && !(r.local.isVpn || r.local.isDatacenter || r.local.isTor || r.local.isAppleRelay || named || r.local.threatListCount)) return false;
      if (filter === 'anonymizer' && !(r.local.isVpn || r.local.isTor || r.local.isAppleRelay || named)) return false;
      if (filter === 'clean' && (r.local.isVpn || r.local.isDatacenter || r.local.isTor || r.local.isAppleRelay || named || r.local.threatListCount || r.local.reserved)) return false;
      if (filter === 'reserved' && !r.local.reserved) return false;
      if (q) {
        const hay = [r.ip, r.live && r.live.rdap.data && r.live.rdap.data.registrantName,
          r.live && r.live.ptr.data && r.live.ptr.data.hostnames.join(' ')].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, filter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  useEffect(() => { setPage(p => Math.min(p, Math.max(0, Math.ceil(filtered.length / PAGE_SIZE) - 1))); }, [filtered.length]);

  const counts = useMemo(() => ({
    flagged: rows.filter(r => r.local.isVpn || r.local.isDatacenter || r.local.isTor || r.local.isAppleRelay || detectNamedVpnProvider(r.live) || r.local.threatListCount).length,
    tor: rows.filter(r => r.local.isTor).length,
    vpn: rows.filter(r => r.local.isVpn || detectNamedVpnProvider(r.live)).length,
    appleRelay: rows.filter(r => r.local.isAppleRelay).length,
    reserved: rows.filter(r => r.local.reserved).length,
  }), [rows]);

  const toggleExpanded = (ip) => {
    // Don't collapse a row out from under someone who is selecting text in it.
    const selection = typeof window !== 'undefined' && window.getSelection && String(window.getSelection());
    if (selection) return;
    setExpanded(cur => (cur === ip ? null : ip));
  };

  const toggleSelect = (ip) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(ip)) next.delete(ip); else next.add(ip);
    return next;
  });

  const exportCsv = useCallback(() => {
    const asOf = datasets.oldestFetchedAt ? new Date(datasets.oldestFetchedAt).toISOString() : 'unknown';
    const hdrs = [
      'IP', 'Assessment', 'Special use', 'VPN list', 'Datacenter list', 'Cloud / CDN', 'Tor exit', 'Tor nickname',
      'Tor fingerprint', 'Relays on address', 'iCloud Private Relay', 'Threat list count', 'Screening datasets as of',
      'RDAP network', 'RDAP registrant', 'RDAP country', 'RDAP registered', 'Abuse contact',
      'BGP ASN', 'BGP AS name', 'Named VPN provider (live)', 'BGP prefix', 'Reverse DNS',
      'Geo (ipwho.is)', 'Geo (freeipapi)', 'Geo (ipapi.is)', 'Geo agreement',
      'Open ports', 'Live lookup at',
    ];
    const yn = v => (v === null ? 'unknown' : v ? 'yes' : 'no');
    const place = g => (g && g.data ? [g.data.city, g.data.region, g.data.country].filter(Boolean).join(', ') : (g ? `error: ${g.error}` : ''));

    const body = filtered.map(r => {
      const l = r.live;
      const rdap = l && l.rdap.data;
      const bgp = l && l.bgp.data;
      return [
        r.ip,
        assess(r.local, l).label,
        r.local.reserved ? r.local.reserved.label : '',
        yn(r.local.isVpn), yn(r.local.isDatacenter), r.local.cloudProvider || '', yn(r.local.isTor),
        r.local.torRelay ? r.local.torRelay.nickname : '',
        r.local.torRelay ? r.local.torRelay.fingerprint : '',
        r.local.torRelay ? r.local.torRelay.relayCount : '',
        yn(r.local.isAppleRelay),
        r.local.threatListCount === null ? 'unknown' : r.local.threatListCount,
        asOf,
        rdap ? rdap.netName : '', rdap ? rdap.registrantName : '', rdap ? rdap.country : '',
        rdap ? rdap.registered : '', rdap ? rdap.abuseEmail : '',
        bgp ? bgp.asn : '', bgp ? bgp.asName : '', (detectNamedVpnProvider(l) || {}).provider || '', bgp ? bgp.prefix : '',
        l && l.ptr.data ? l.ptr.data.hostnames.join(' ') : '',
        l ? place(l.geo[0]) : '', l ? place(l.geo[1]) : '', l ? place(l.geo[2]) : '',
        l ? geoAgreement(l.geo).level : '',
        l && l.shodan.data ? l.shodan.data.ports.join(' ') : '',
        l ? new Date(l.enrichedAt).toISOString() : '',
      ];
    });

    const csv = [hdrs, ...body]
      .map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: 'ip-intelligence-export.csv',
    });
    a.click();
    URL.revokeObjectURL(a.href);
  }, [filtered, datasets.oldestFetchedAt]);

  const unenrichedSelected = [...selected].filter(ip => !liveByIp[ip] && !classifyReserved(ip));

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", color: INK }}>
      <Navbar pageTitle="IP and Domain Intelligence" />
      {/* Row hover/focus feedback — the rest of this page uses inline styles,
          which can't express :hover. */}
      <style>{`
        .ip-row:hover { background: #f8fafc !important; }
        .ip-row:focus-visible { outline: 2px solid #6366f1; outline-offset: -2px; }
      `}</style>

      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '24px 24px 72px' }}>
        <Disclosure />
        <DatasetChip datasets={datasets} />
        <SelfCheck datasets={datasets} />

        {/* Input */}
        <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '18px 20px', marginBottom: 20 }}>
          <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: 8, padding: 3, marginBottom: 14 }}>
            {[['ip', 'IP addresses'], ['domain', 'Domains & email']].map(([value, label]) => (
              <button
                key={value}
                onClick={() => { setMode(value); setNotice(''); }}
                style={{
                  background: mode === value ? '#fff' : 'transparent',
                  color: mode === value ? INK : MUTED,
                  border: 'none', borderRadius: 6, padding: '6px 16px',
                  fontSize: 13, fontWeight: mode === value ? 700 : 600, cursor: 'pointer',
                  boxShadow: mode === value ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {mode === 'domain' && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 9,
              background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
              borderRadius: 8, padding: '9px 12px', marginBottom: 12, fontSize: 12.5, lineHeight: 1.55,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}>
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>
                <strong>Domain lookups always use the network.</strong> There is no offline list that answers who
                runs a domain&rsquo;s mail, so unlike IP screening there is no confidential mode here. For an email
                address only the <strong>domain</strong> is sent — the mailbox name never leaves this device.
              </span>
            </div>
          )}

          <label htmlFor="ip-input" style={{ display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
            {mode === 'ip' ? 'IP addresses' : 'Domains & email addresses'}
          </label>
          <textarea
            id="ip-input"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={mode === 'ip'
              ? '8.8.8.8\n198.98.51.189\n192.168.1.1\n\nPaste one per line, comma-separated, or an entire log — addresses are extracted automatically and de-duplicated.'
              : 'suspect@theirdomain.com\ntheirdomain.com\nhttps://example.org/page\n\nPaste email addresses, domains or URLs — the domain is extracted automatically.'}
            rows={6}
            style={{
              width: '100%', boxSizing: 'border-box', resize: 'vertical',
              border: `1px solid ${BORDER}`, borderRadius: 8, padding: '10px 12px',
              fontSize: 13, fontFamily: 'monospace', lineHeight: 1.6, color: INK, background: '#f8fafc',
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: fileBusy ? 'default' : 'pointer' }}>
              <span style={{
                border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px 12px', fontSize: 12.5,
                fontWeight: 600, color: fileBusy ? '#94a3b8' : '#475569', background: '#fff',
              }}>
                {fileBusy ? 'Reading…' : 'Upload file'}
              </span>
              <input
                type="file"
                accept=".txt,.csv,.xlsx"
                disabled={fileBusy}
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </label>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>
              .txt, .csv, or .xlsx — {mode === 'ip' ? 'addresses' : 'domains/emails'} are extracted automatically and added to the box above
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
            {mode === 'ip' ? (
              <>
                <button
                  onClick={screenAndEnrich}
                  disabled={!text.trim()}
                  title="Screens locally, then sends every non-private address to the third-party services listed in each result."
                  style={{
                    background: text.trim() ? '#4f46e5' : '#cbd5e1', color: '#fff', border: 'none',
                    borderRadius: 8, padding: '9px 20px', fontSize: 14, fontWeight: 700,
                    cursor: text.trim() ? 'pointer' : 'default',
                  }}
                >
                  Screen + live lookup
                </button>
                <button
                  onClick={() => screen(text)}
                  disabled={!text.trim()}
                  style={{
                    background: '#fff', color: text.trim() ? '#4f46e5' : '#94a3b8',
                    border: `1.5px solid ${text.trim() ? '#c7d2fe' : BORDER}`,
                    borderRadius: 8, padding: '9px 18px', fontSize: 14, fontWeight: 700,
                    cursor: text.trim() ? 'pointer' : 'default',
                  }}
                >
                  Screen addresses
                </button>
              </>
            ) : (
              <button
                onClick={runDomains}
                disabled={!text.trim() || domainBusy}
                style={{
                  background: text.trim() && !domainBusy ? '#4f46e5' : '#cbd5e1', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '9px 20px', fontSize: 14, fontWeight: 700,
                  cursor: text.trim() && !domainBusy ? 'pointer' : 'default',
                }}
              >
                {domainBusy ? 'Looking up…' : 'Look up domains'}
              </button>
            )}
            {(ips.length > 0 || domainEntries.length > 0) && (
              <button
                onClick={() => {
                  setIps([]); setText(''); setLiveByIp({}); setSelected(new Set()); setExpanded(null);
                  setNotice(''); setDomainEntries([]); setDomainResults({});
                }}
                style={{ background: 'none', border: 'none', color: MUTED, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Clear
              </button>
            )}
            {mode === 'ip' && (
              <span style={{ fontSize: 12, color: MUTED, marginLeft: 'auto', textAlign: 'right' }}>
                <strong style={{ color: '#92400e' }}>Screen + live lookup</strong> sends every address out.{' '}
                <strong style={{ color: '#475569' }}>Screen addresses</strong> stays on this device.
              </span>
            )}
          </div>
          {notice && <p style={{ margin: '10px 0 0', fontSize: 12.5, color: MUTED }}>{notice}</p>}
        </div>

        {/* Domain / email results */}
        {mode === 'domain' && domainEntries.length > 0 && (
          <>
            <div style={{
              display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
              background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12,
              padding: '12px 16px', marginBottom: 12,
            }}>
              <strong style={{ fontSize: 14 }}>
                {domainEntries.length} {domainEntries.length === 1 ? 'entry' : 'entries'}
              </strong>
              <span style={{ fontSize: 12.5, color: MUTED }}>
                {Object.keys(domainResults).length} of {new Set(domainEntries.map(e => e.domain)).size} domains resolved
              </span>
              <button
                onClick={exportDomainCsv}
                style={{ marginLeft: 'auto', background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 7, padding: '7px 14px', fontSize: 12.5, fontWeight: 600, color: '#475569', cursor: 'pointer' }}
              >
                Export CSV
              </button>
            </div>

            {domainEntries.map(entry => (
              <DomainCard
                key={`${entry.kind}:${entry.input}`}
                entry={entry}
                result={domainResults[entry.domain]}
                indexes={datasets.indexes}
              />
            ))}

            <p style={{ fontSize: 12, color: MUTED, lineHeight: 1.65, margin: '4px 0 0' }}>
              <strong style={{ color: '#475569' }}>What this does and doesn&rsquo;t prove.</strong> MX records show
              who handles mail for the domain <em>right now</em> — they can be changed at any time, so record the
              lookup date. Registrant details are redacted for most domains under GDPR and ICANN policy, so the
              registrar&rsquo;s abuse contact is usually the only route to them. Nothing here confirms a mailbox
              exists or identifies who uses it; that needs legal process to the mail provider.
            </p>
          </>
        )}

        {/* Results */}
        {mode === 'ip' && rows.length > 0 && (
          <>
            <div style={{
              display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
              background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12,
              padding: '12px 16px', marginBottom: 12,
            }}>
              <strong style={{ fontSize: 14 }}>{rows.length.toLocaleString()} address{rows.length === 1 ? '' : 'es'}</strong>
              <span style={{ fontSize: 12.5, color: MUTED }}>
                {counts.flagged} flagged · {counts.tor} Tor · {counts.vpn} VPN · {counts.appleRelay} iCloud Relay · {counts.reserved} internal
              </span>

              <select
                value={filter}
                onChange={e => { setFilter(e.target.value); setPage(0); }}
                style={{ border: `1px solid ${BORDER}`, borderRadius: 7, padding: '6px 10px', fontSize: 12.5, color: '#475569', background: '#fff' }}
              >
                <option value="all">All</option>
                <option value="flagged">Flagged only</option>
                <option value="anonymizer">VPN, Tor, or iCloud Relay</option>
                <option value="clean">No match</option>
                <option value="reserved">Internal / special use</option>
              </select>

              <input
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
                placeholder="Search IP, owner, hostname…"
                style={{ border: `1px solid ${BORDER}`, borderRadius: 7, padding: '6px 10px', fontSize: 12.5, minWidth: 200, flex: '0 1 240px' }}
              />

              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  onClick={() => runEnrich(unenrichedSelected)}
                  disabled={!unenrichedSelected.length}
                  title={unenrichedSelected.length ? `Sends ${unenrichedSelected.length} address(es) to third-party services` : 'Select rows to enrich'}
                  style={{
                    background: unenrichedSelected.length ? '#4f46e5' : '#e2e8f0',
                    color: unenrichedSelected.length ? '#fff' : '#94a3b8',
                    border: 'none', borderRadius: 7, padding: '7px 14px', fontSize: 12.5, fontWeight: 700,
                    cursor: unenrichedSelected.length ? 'pointer' : 'default',
                  }}
                >
                  Live lookup{unenrichedSelected.length ? ` (${unenrichedSelected.length})` : ''}
                </button>
                <button
                  onClick={exportCsv}
                  style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 7, padding: '7px 14px', fontSize: 12.5, fontWeight: 600, color: '#475569', cursor: 'pointer' }}
                >
                  Export CSV
                </button>
              </div>

              <span style={{
                flexBasis: '100%', fontSize: 11.5,
                color: lookupsToday >= DAILY_SOFT_LIMIT ? '#b45309' : MUTED,
              }}>
                {lookupsToday >= DAILY_SOFT_LIMIT
                  ? `Free daily lookup allowance used (${lookupsToday}). Geolocation will return errors until it resets at 00:00 UTC — registration, DNS and port data are unaffected.`
                  : `${lookupsToday} of ~${DAILY_SOFT_LIMIT} free live lookups used today (resets 00:00 UTC).`}
              </span>
            </div>

            {!datasets.complete && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 9,
                background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
                borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 12.5, lineHeight: 1.55,
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                  style={{ flexShrink: 0, marginTop: 1 }}>
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>
                  {datasets.status === 'refreshing' || datasets.status === 'loading'
                    ? <><strong>Screening lists are still downloading.</strong> Rows below read “Not screened”
                        until they arrive, then update themselves — you don’t need to run this again. Wait for the
                        badge above to say “current” before exporting.</>
                    : <><strong>Screening lists are not all loaded.</strong> Missing checks read “unknown”, never
                        “clean”, so treat the verdicts below as incomplete. Press Refresh above to try again.</>}
                </span>
              </div>
            )}

            <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
                    <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                      <th style={{ width: 34, padding: '10px 0 10px 14px' }}>
                        <input
                          type="checkbox"
                          checked={pageRows.length > 0 && pageRows.every(r => selected.has(r.ip))}
                          onChange={e => setSelected(prev => {
                            const next = new Set(prev);
                            pageRows.forEach(r => (e.target.checked ? next.add(r.ip) : next.delete(r.ip)));
                            return next;
                          })}
                        />
                      </th>
                      {['IP address', 'Assessment', 'Threat lists', 'Owner / network', 'Location', ''].map((h, i) => (
                        <th key={h || i} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map(row => {
                      const a = assess(row.local, row.live);
                      const tone = TONES[a.tone];
                      const rdap = row.live && row.live.rdap.data;
                      const geo = row.live && row.live.geo.find(g => g.data);
                      const isOpen = expanded === row.ip;
                      return (
                        <Fragment key={row.ip}>
                        <tr
                          className="ip-row"
                          tabIndex={0}
                          aria-expanded={isOpen}
                          onClick={() => toggleExpanded(row.ip)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpanded(row.ip); }
                          }}
                          style={{
                            borderBottom: `1px solid ${isOpen ? 'transparent' : '#f1f5f9'}`,
                            background: isOpen ? '#fafbff' : '#fff',
                            cursor: 'pointer',
                          }}
                        >
                          {/* Selecting rows must not also expand them. */}
                          <td
                            onClick={e => e.stopPropagation()}
                            style={{ padding: '9px 0 9px 14px', verticalAlign: 'top', cursor: 'default' }}
                          >
                            <input
                              type="checkbox"
                              checked={selected.has(row.ip)}
                              onChange={() => toggleSelect(row.ip)}
                              aria-label={`Select ${row.ip}`}
                            />
                          </td>
                          <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                            {row.ip}
                          </td>
                          <td style={{ padding: '9px 14px', verticalAlign: 'top' }}>
                            <Badge text={a.label} color={tone.color} bg={tone.bg} />
                          </td>
                          <td style={{ padding: '9px 14px', verticalAlign: 'top' }}>
                            {row.local.threatListCount === null
                              ? <span style={{ color: '#94a3b8', fontSize: 12 }}>unknown</span>
                              : row.local.threatListCount
                                ? <Badge text={`${row.local.threatListCount} lists`} color={TONES[threatTone(row.local.threatListCount)].color} bg={TONES[threatTone(row.local.threatListCount)].bg} />
                                : <span style={{ color: '#94a3b8', fontSize: 12 }}>none</span>}
                          </td>
                          <td style={{ padding: '9px 14px', verticalAlign: 'top', color: rdap ? INK : '#94a3b8', fontSize: 12.5 }}>
                            {rdap ? (rdap.registrantName || rdap.netName || '—')
                              : row.status === 'loading' ? 'looking up…'
                              : row.local.reserved ? '—' : 'not looked up'}
                          </td>
                          <td style={{ padding: '9px 14px', verticalAlign: 'top', color: geo ? INK : '#94a3b8', fontSize: 12.5 }}>
                            {geo ? [geo.data.city, geo.data.country].filter(Boolean).join(', ') || '—'
                              : row.status === 'loading' ? '…' : '—'}
                          </td>
                          <td style={{ padding: '9px 14px', textAlign: 'right', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                            <button
                              // The row handles this too; stop it here so the
                              // two don't fire and cancel each other out.
                              onClick={e => { e.stopPropagation(); toggleExpanded(row.ip); }}
                              tabIndex={-1}
                              style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '3px 10px', fontSize: 11.5, fontWeight: 600, color: '#475569', cursor: 'pointer' }}
                            >
                              {isOpen ? '▲ Hide' : '▼ Details'}
                            </button>
                          </td>
                        </tr>
                        {/* Detail sits directly under its own row, so it stays
                            in view no matter how long the list is. */}
                        {isOpen && (
                          <tr>
                            <td colSpan={7} style={{ padding: 0, borderBottom: `1px solid ${BORDER}` }}>
                              <DetailPanel row={row} onEnrich={runEnrich} />
                            </td>
                          </tr>
                        )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '12px', borderTop: `1px solid ${BORDER}` }}>
                  <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                    style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '5px 12px', fontSize: 12.5, cursor: page === 0 ? 'default' : 'pointer', opacity: page === 0 ? 0.5 : 1 }}>
                    ← Prev
                  </button>
                  <span style={{ fontSize: 12.5, color: MUTED }}>Page {page + 1} of {totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                    style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '5px 12px', fontSize: 12.5, cursor: page >= totalPages - 1 ? 'default' : 'pointer', opacity: page >= totalPages - 1 ? 0.5 : 1 }}>
                    Next →
                  </button>
                </div>
              )}
            </div>

            <p style={{ fontSize: 12, color: MUTED, lineHeight: 1.65, margin: '16px 0 0' }}>
              <strong style={{ color: '#475569' }}>Weight this correctly.</strong> Geolocation is a commercial
              estimate, frequently wrong at city level and sometimes wrong at country level — it is never subscriber
              identity. VPN and datacenter verdicts come from public range lists that lag reality and cannot see
              residential or mobile proxies, so “no match” is not proof an address is a home connection. Every
              screening verdict is only as current as the dataset date shown above, which is recorded in the CSV
              export. Attribution to a person requires legal process to the provider.
            </p>
          </>
        )}

        {((mode === 'ip' && rows.length === 0) || (mode === 'domain' && domainEntries.length === 0)) && (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: MUTED }}>
            {mode === 'ip' ? (
              <>
                <p style={{ fontSize: 14, margin: '0 0 6px' }}>Paste one or more IP addresses to begin.</p>
                <p style={{ fontSize: 12.5, margin: 0 }}>
                  Screening runs against {Object.values(DATASETS).length} public datasets held in this browser ·
                  IPv4 for list checks · live lookup is opt-in per address
                </p>
              </>
            ) : (
              <>
                <p style={{ fontSize: 14, margin: '0 0 6px' }}>Paste an email address, domain or URL to begin.</p>
                <p style={{ fontSize: 12.5, margin: 0 }}>
                  Returns who actually handles the mail (who to serve), registration and abuse contact,
                  nameservers, SPF/DMARC, and the addresses it resolves to
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
